import { useState } from "react";
import API from "../../api/api";

import {
    FaLock,
    FaEye,
    FaEyeSlash,
    FaCheckCircle,
    FaExclamationCircle
} from "react-icons/fa";


/* =========================================================
   PASSWORD INPUT
   IMPORTANT:
   This must stay OUTSIDE Settings()
   so React does not remount it on every keystroke.
========================================================= */

function PasswordInput({
    label,
    value,
    onChange,
    show,
    setShow,
    autoComplete
}) {

    return (

        <div style={field}>

            <label style={labelStyle}>
                {label}
            </label>

            <div style={inputWrapper}>

                <FaLock style={lockIcon} />

                <input
                    type={
                        show
                            ? "text"
                            : "password"
                    }
                    value={value}
                    onChange={(e) =>
                        onChange(e.target.value)
                    }
                    autoComplete={autoComplete}
                    style={input}
                />

                <button
                    type="button"
                    onClick={() =>
                        setShow(!show)
                    }
                    style={eyeButton}
                    aria-label={
                        show
                            ? "Hide password"
                            : "Show password"
                    }
                >

                    {show
                        ? <FaEyeSlash />
                        : <FaEye />
                    }

                </button>

            </div>

        </div>
    );
}


/* =========================================================
   SETTINGS
========================================================= */

export default function Settings() {

    const [currentPassword, setCurrentPassword] =
        useState("");

    const [newPassword, setNewPassword] =
        useState("");

    const [confirmPassword, setConfirmPassword] =
        useState("");

    const [showCurrent, setShowCurrent] =
        useState(false);

    const [showNew, setShowNew] =
        useState(false);

    const [showConfirm, setShowConfirm] =
        useState(false);

    const [loading, setLoading] =
        useState(false);

    const [success, setSuccess] =
        useState("");

    const [error, setError] =
        useState("");


    /* =====================================================
       CHANGE PASSWORD
    ===================================================== */

    const handleSubmit = async (e) => {

        e.preventDefault();

        setSuccess("");
        setError("");


        /* ================= VALIDATION ================= */

        if (
            !currentPassword ||
            !newPassword ||
            !confirmPassword
        ) {

            setError(
                "Please fill in all password fields."
            );

            return;
        }


        if (newPassword.length < 8) {

            setError(
                "Your new password must be at least 8 characters long."
            );

            return;
        }


        if (newPassword !== confirmPassword) {

            setError(
                "The new passwords do not match."
            );

            return;
        }


        if (newPassword === currentPassword) {

            setError(
                "Your new password must be different from your current password."
            );

            return;
        }


        /* ================= REQUEST ================= */

        try {

            setLoading(true);

            await API.post(
                "/auth/change-password",
                {
                    currentPassword,
                    newPassword,
                    confirmPassword
                }
            );


            setSuccess(
                "Your password has been changed successfully."
            );


            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");


        } catch (err) {

            console.error(
                "Change password error:",
                err
            );

            setError(
                err.response?.data?.error ||
                "Unable to change your password. Please try again."
            );

        } finally {

            setLoading(false);

        }

    };


    return (

        <div style={page}>

            <div style={header}>

                <h2 style={title}>
                    Settings
                </h2>

                <p style={subtitle}>
    Manage your coordinator account and security.
</p>

            </div>


            <div style={card}>

                <div style={cardHeader}>

                    <div style={iconBox}>
                        <FaLock />
                    </div>

                    <div>

                        <h3 style={cardTitle}>
                            Change Password
                        </h3>

                        <p style={cardDescription}>
                            Change the password provided by your
                            administrator.
                        </p>

                    </div>

                </div>


                <form
                    onSubmit={handleSubmit}
                    style={form}
                >

                    <PasswordInput
                        label="Current Password"
                        value={currentPassword}
                        onChange={setCurrentPassword}
                        show={showCurrent}
                        setShow={setShowCurrent}
                        autoComplete="current-password"
                    />


                    <PasswordInput
                        label="New Password"
                        value={newPassword}
                        onChange={setNewPassword}
                        show={showNew}
                        setShow={setShowNew}
                        autoComplete="new-password"
                    />


                    <PasswordInput
                        label="Confirm New Password"
                        value={confirmPassword}
                        onChange={setConfirmPassword}
                        show={showConfirm}
                        setShow={setShowConfirm}
                        autoComplete="new-password"
                    />


                    <div style={passwordHint}>
                        Password must contain at least 8 characters.
                    </div>


                    {error && (

                        <div style={errorBox}>

                            <FaExclamationCircle />

                            <span>
                                {error}
                            </span>

                        </div>

                    )}


                    {success && (

                        <div style={successBox}>

                            <FaCheckCircle />

                            <span>
                                {success}
                            </span>

                        </div>

                    )}


                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            ...button,
                            opacity:
                                loading ? 0.65 : 1
                        }}
                    >

                        {loading
                            ? "Changing Password..."
                            : "Change Password"
                        }

                    </button>

                </form>

            </div>

        </div>

    );
}


/* =========================================================
   STYLES
========================================================= */

const page = {
    minHeight: "100%",
    padding: 10,
    background: "#ffffff",
    fontFamily: "Inter, system-ui"
};

const header = {
    marginBottom: 22
};

const title = {
    margin: 0,
    fontSize: 24,
    fontWeight: 700,
    color: "#111827"
};

const subtitle = {
    marginTop: 6,
    marginBottom: 0,
    fontSize: 14,
    color: "#64748b"
};

const card = {
    maxWidth: 680,
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    padding: 24,
    boxShadow: "0 6px 20px rgba(0,0,0,0.05)"
};

const cardHeader = {
    display: "flex",
    alignItems: "center",
    gap: 14,
    marginBottom: 24
};

const iconBox = {
    width: 46,
    height: 46,
    borderRadius: 12,
    background: "#fff7ed",
    color: "#c75028",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 19,
    flexShrink: 0
};

const cardTitle = {
    margin: 0,
    fontSize: 18,
    fontWeight: 700,
    color: "#111827"
};

const cardDescription = {
    margin: "4px 0 0",
    fontSize: 13,
    color: "#64748b"
};

const form = {
    display: "flex",
    flexDirection: "column",
    gap: 2
};

const field = {
    marginBottom: 14
};

const labelStyle = {
    display: "block",
    marginBottom: 6,
    fontSize: 13,
    fontWeight: 600,
    color: "#374151"
};

const inputWrapper = {
    position: "relative",
    display: "flex",
    alignItems: "center"
};

const lockIcon = {
    position: "absolute",
    left: 12,
    color: "#94a3b8",
    fontSize: 13,
    pointerEvents: "none"
};

const input = {
    width: "100%",
    boxSizing: "border-box",
    padding: "11px 42px 11px 36px",
    border: "1px solid #d1d5db",
    borderRadius: 9,
    outline: "none",
    fontSize: 14,
    color: "#111827",
    background: "#ffffff"
};

const eyeButton = {
    position: "absolute",
    right: 8,
    top: "50%",
    transform: "translateY(-50%)",
    border: "none",
    background: "transparent",
    color: "#64748b",
    cursor: "pointer",
    padding: 7,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
};

const passwordHint = {
    fontSize: 12,
    color: "#64748b",
    marginTop: 2,
    marginBottom: 14
};

const errorBox = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    borderRadius: 9,
    background: "#fef2f2",
    color: "#b91c1c",
    fontSize: 13,
    marginBottom: 14
};

const successBox = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    borderRadius: 9,
    background: "#f0fdf4",
    color: "#15803d",
    fontSize: 13,
    marginBottom: 14
};

const button = {
    marginTop: 4,
    padding: "11px 18px",
    border: "none",
    borderRadius: 9,
    background: "#c75028",
    color: "#ffffff",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer"
};